import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  AlertTriangle,
  KeyRound,
  Smartphone,
  Activity,
  Lock,
  HardHat,
  Home as HomeIcon,
  IndianRupee,
  ShieldCheck,
  Workflow,
  Cable,
  Network,
  Cpu,
  QrCode,
} from "lucide-react";
import { PlatformShellHeader, PlatformShellFooter, useSeo } from "./PlatformShell";

/* ===================== USE-CASE CONTENT ===================== */

const USE_CASES = {
  "builder-sites": {
    icon: HardHat,
    eyebrow: "For Real Estate Developers & Builders",
    h1: "Force the payment schedule with the gate.",
    sub: "Tie BBC barrier access at your project site to the buyer's payment ledger. When an instalment is overdue, the project gate stops opening for that buyer — restored the second finance signs off.",
    metaTitle: "Boom Barriers for Builder Sites · Force Payment Schedule with Remote-Lock Gates · BBC",
    metaDesc:
      "How real estate developers use Boom Barrier Company barriers with remote payment-lock to enforce instalment schedules. CRM-bound access, full audit log, restored automatically on payment clearance.",
    canonical: "https://www.boombarriercompany.com/use-cases/builder-sites",
    image: "https://www.boombarriercompany.com/branded/hero.png",
    keyword: "boom barrier for builder sites",
    problem: {
      title: "The collection problem on a 200-flat project.",
      body: "Construction-linked instalments stack up — slab milestones, finishing, possession. Buyers stall. Site supervisors get caught in the middle. Finance teams chase reminders. By the time legal options are on the table, ₹40–80L is already overdue across the project. The gate at site is the only physical lever you have — and right now, it's run by a guard with a pen-and-paper register.",
      pains: [
        "Site supervisor caught between buyer pressure and finance",
        "Manual entry / exit lists that nobody updates",
        "No audit trail when buyers claim they 'were never told'",
        "Finance has no way to enforce — only escalate",
      ],
    },
    solution: {
      title: "How BBC barriers turn the gate into a payment lever.",
      body: "Every BBC barrier ships with the BBC Trust smart controller and our remote-control layer. When a buyer's instalment is overdue, finance flags the buyer in their CRM or accounts ledger. BBC Trust syncs the status to the project gate — that buyer's vehicle access is automatically disabled. The moment the payment clears, access is restored. No confrontation at the gate, no supervisor judgement calls, no missed audit entries.",
      steps: [
        {
          n: "01",
          t: "Buyer registered at gate",
          d: "On allotment, the buyer's vehicle and access token (RFID / ANPR / phone QR) is bound to their CRM record in BBC Trust.",
        },
        {
          n: "02",
          t: "Finance flags overdue",
          d: "Your accounts team marks the buyer's instalment overdue in your CRM/ERP — BBC Trust receives the status via webhook or scheduled sync.",
        },
        {
          n: "03",
          t: "Gate access auto-disabled",
          d: "The buyer's vehicle is silently de-listed at the project gate. The barrier still operates for everyone else — only the flagged tokens are blocked.",
        },
        {
          n: "04",
          t: "Payment clears → access restored",
          d: "Finance marks the instalment paid → BBC Trust re-enables the token in real time. Every state change is signed and logged.",
        },
      ],
    },
    why: {
      title: "Why this works on real construction sites.",
      points: [
        { icon: KeyRound, t: "Per-buyer, not per-gate", d: "Only flagged buyers are blocked. Workers, vendors, and paid-up buyers move freely." },
        { icon: Smartphone, t: "Controlled from the office", d: "Finance team operates this from BBC Trust — no site visit, no instructions to the security guard." },
        { icon: Lock, t: "Signed audit log", d: "Every enable/disable action is recorded with timestamp, user, and reason. Defensible in arbitration or RERA proceedings." },
        { icon: Activity, t: "Real-time, not batch", d: "Status changes propagate within seconds — no overnight sync gaps where a paid-up buyer is locked out." },
        { icon: Cable, t: "Plugs into your CRM", d: "REST API + webhook hooks for Salesforce, Zoho, custom-built ERPs, and the real estate CRMs commonly used in India." },
        { icon: ShieldCheck, t: "RERA & legal-safe", d: "Access is bound to documented payment status — not arbitrary supervisor decisions. Far harder to contest." },
      ],
    },
    objections: [
      {
        q: "What if the buyer claims they paid and the gate didn't open?",
        a: "Every barrier action is signed and logged with timestamp + reason in BBC Trust. The audit log is exportable and shows exactly when the disable was set, who set it, and which payment status triggered it. If finance marks paid, the gate opens within seconds — and that re-enable is logged too.",
      },
      {
        q: "What about workers, vendors, and visitors?",
        a: "Remote payment-lock applies only to flagged buyer tokens. Workers, contractors, vendors and visitors operate through your normal access stack (RFID cards, ANPR, QR codes) — they are unaffected.",
      },
      {
        q: "Is this legal in India?",
        a: "Yes — access at private project sites is at the developer's discretion under their possession terms. Most builder-buyer agreements already mention access privileges tied to payment compliance. BBC's audit log makes enforcement clean, transparent and contestable.",
      },
      {
        q: "What happens if the internet at the project site goes down?",
        a: "The BBC controller caches the latest access policy locally — so if connectivity drops, gates continue enforcing the last known policy. State changes sync the moment connectivity returns. Access never fails 'open' on its own.",
      },
    ],
    cta: {
      heading: "Get a tailored pitch for your project.",
      body: "Tell us the project size, gate count, and your CRM stack — we'll put together a deployment plan and a 15-minute integration demo with your accounts team.",
      label: "Talk to a Project Specialist",
    },
  },

  "rental-properties": {
    icon: HomeIcon,
    eyebrow: "For PGs, Co-Living, Rental Builders & Landlords",
    h1: "Rent overdue? Gate stops opening.",
    sub: "Tie vehicle and visitor access at your property to each tenant's rent status. A 30-day overdue tenant is locked out automatically — restored the moment the rent clears.",
    metaTitle: "Boom Barriers for Rental Properties · Remote Rent-Lock Access · BBC",
    metaDesc:
      "How PG owners, co-living operators and rental builders use Boom Barrier Company barriers with remote rent-lock to enforce on-time rent collection. Per-tenant access policies, automatic re-enable on payment.",
    canonical: "https://www.boombarriercompany.com/use-cases/rental-properties",
    image: "https://www.boombarriercompany.com/branded/hero.png",
    keyword: "boom barrier for rental properties",
    problem: {
      title: "The collection problem in rental housing.",
      body: "Rent is supposed to be simple — but the second tenancy hits month 4 or 5, late payments start. A 30-day chase begins: WhatsApp reminders, gentle calls, finally a visit. By month 60+ days overdue, you're either accepting the loss or starting eviction. Meanwhile, every other tenant sees what they can get away with.",
      pains: [
        "Manual rent reminders are a part-time job for the operator",
        "No way to escalate before legal eviction",
        "Other tenants notice and follow the same delay pattern",
        "Confrontation at the gate damages relationships and reputation",
      ],
    },
    solution: {
      title: "How BBC barriers tie access to rent status.",
      body: "Each tenant's vehicle and access token (RFID card, ANPR plate, phone QR) is bound to their tenant record in BBC Trust. When rent crosses your overdue threshold — say 15, 30, or 45 days — that tenant's vehicle access at the property gate is automatically de-listed. The barrier still opens for everyone else. The moment rent clears, access is restored within seconds. No confrontation, no judgement calls, no awkward visits.",
      steps: [
        {
          n: "01",
          t: "Tenant onboarded",
          d: "Tenant's vehicle and access token registered in BBC Trust, bound to their rental ledger and your preferred rent-collection app.",
        },
        {
          n: "02",
          t: "Rent overdue threshold hit",
          d: "Rent crosses your configured threshold (e.g. 30 days). BBC Trust auto-flags the tenant's tokens.",
        },
        {
          n: "03",
          t: "Vehicle access disabled",
          d: "Tenant's RFID / ANPR / QR is silently de-listed at the property gate. They can still walk in, but their car doesn't get parking access until rent clears.",
        },
        {
          n: "04",
          t: "Payment clears → access restored",
          d: "Rent payment marked received → BBC Trust re-enables the tokens in real time. Audit log keeps the full state history.",
        },
      ],
    },
    why: {
      title: "Why rent-lock works for rental operators.",
      points: [
        { icon: HomeIcon, t: "Per-tenant, not per-property", d: "Only overdue tenants are affected. Paying tenants don't even notice — service stays normal." },
        { icon: Smartphone, t: "Run from your phone", d: "Owners and managers operate this from BBC Trust app. No on-site visit, no awkward gate conversations." },
        { icon: Activity, t: "Re-enables instantly", d: "Payment cleared at 11:47pm? Gate is back on for that tenant by 11:47pm — not next morning." },
        { icon: Lock, t: "Soft escalation", d: "A blocked gate is a far softer signal than an eviction notice — most tenants pay within hours of being affected." },
        { icon: Cable, t: "Works with your rent app", d: "API hooks into NoBroker, NestAway, custom landlord apps and most rent-collection tools used in India." },
        { icon: ShieldCheck, t: "Audit-trail backed", d: "Every disable / enable signed and logged. Defensible if a tenant disputes the lock-out." },
      ],
    },
    objections: [
      {
        q: "Is this legal in a rental setup?",
        a: "Yes — most rental agreements specify amenities and parking access as conditional on rent compliance. BBC Trust restricts vehicle/parking access, not personal entry to the rented unit. The audit log makes enforcement transparent and contestable.",
      },
      {
        q: "What about guests of the tenant?",
        a: "Guest access is handled separately — typically through visitor passes or a manual override at the gate. Rent-lock only affects the tenant's own registered tokens.",
      },
      {
        q: "Can the tenant walk in even if locked?",
        a: "Yes — rent-lock only affects vehicular access through the boom barrier. Personal pedestrian entry is unaffected. This makes the enforcement proportionate.",
      },
      {
        q: "What if the rent-collection app doesn't have an API?",
        a: "BBC Trust supports manual flagging from a web dashboard — your manager or accountant can mark a tenant overdue in one click. We also offer CSV-import sync for landlords using spreadsheets.",
      },
    ],
    cta: {
      heading: "Built for serious rental operators.",
      body: "Tell us your property type, unit count, and rent-collection workflow — we'll show you exactly how rent-lock would integrate, and how fast it pays for itself.",
      label: "Talk to a Rental Specialist",
    },
  },

  "pay-per-use": {
    icon: IndianRupee,
    eyebrow: "For Pay-Per-Use, Toll, Event & Parking Operators",
    h1: "Operator-controlled gates. From anywhere.",
    sub: "Open, close, lock or schedule any BBC barrier from the BBC Trust app or your backend — for pay-per-entry parking, event venues, toll lanes, commercial yards and any site where access has to follow payment.",
    metaTitle: "Remote-Controlled Boom Barriers for Toll, Parking & Pay-Per-Use · BBC",
    metaDesc:
      "How parking, toll and event operators use Boom Barrier Company barriers with full remote-control — one-tap open/close, schedule windows, API hooks into payments, and an audit log of every action.",
    canonical: "https://www.boombarriercompany.com/use-cases/pay-per-use",
    image: "https://www.boombarriercompany.com/branded/hero.png",
    keyword: "remote controlled boom barrier",
    problem: {
      title: "The cost of running a gate the old way.",
      body: "A pay-per-use barrier without remote control is fundamentally a guard on a stool. Hours of manual operation, no audit, missed payments at busy times, and zero ability to schedule access windows without standing at the lane. The cost of the guard outruns the cost of the barrier within a year — and you still don't have the data.",
      pains: [
        "Manual gate operation = labour cost stacking month after month",
        "No proof of payment-to-entry linkage when disputes happen",
        "Can't enforce time windows (e.g. event entry only between 6–10pm) without a human gatekeeper",
        "Multi-site operators have no fleet visibility",
      ],
    },
    solution: {
      title: "What full remote-control looks like.",
      body: "Every BBC barrier exposes a complete control surface to BBC Trust and to your own backend via API. One-tap open / close from the app. Schedule windows by gate, by zone, by weekday. Plug payment events directly into the gate — when a customer pays at your kiosk or app, the gate opens automatically with a full signed log. When the operator wants to lock down, one tap. When the event is over, one tap. No on-site guard needed for routine operation.",
      steps: [
        {
          n: "01",
          t: "Connect once",
          d: "BBC Trust connects to your payments / ticketing / parking system via REST API or webhook in under a day.",
        },
        {
          n: "02",
          t: "Define your policy",
          d: "Set the rules: pay-per-entry, time-window schedules, vehicle-class allow-lists, VIP overrides — all from the dashboard.",
        },
        {
          n: "03",
          t: "Operator-controlled",
          d: "Operators open, close, lock or unlock any gate from BBC Trust. Schedule changes propagate instantly. No site visit needed.",
        },
        {
          n: "04",
          t: "Audit & reconcile",
          d: "Every barrier action is signed and logged — perfect reconciliation between payments and entries, exportable to CSV / Tally / your accountant.",
        },
      ],
    },
    why: {
      title: "Why remote-control is non-negotiable for pay-per-use.",
      points: [
        { icon: Smartphone, t: "One-tap operator control", d: "Open, close, lock, schedule — anywhere, anytime. From the phone or the dashboard." },
        { icon: Workflow, t: "Schedule windows", d: "Set entry windows by day, by gate, by event — automatic enforcement, no human at the lane." },
        { icon: Cable, t: "Payments-bound", d: "Webhook from your payment system → gate opens within seconds. Full receipt-to-entry linkage." },
        { icon: Network, t: "Multi-site fleet view", d: "Run 5 or 50 gates across cities from one screen — uptime, payment events, and audit logs in one place." },
        { icon: Lock, t: "Signed audit log", d: "Every barrier action is logged with timestamp + user + reason. Reconciliation is no longer a guess." },
        { icon: ShieldCheck, t: "Fail-safe behaviour", d: "If connectivity drops, gates enforce last-known policy. Never fails open without operator consent." },
      ],
    },
    objections: [
      {
        q: "What if I already use FASTag / RFID for the gate?",
        a: "BBC remote-control sits ALONGSIDE your existing access stack. FASTag and RFID still handle normal in-flow — BBC adds operator override, scheduling, and the API/audit layer on top. Nothing to rip out.",
      },
      {
        q: "How fast does the gate respond to a remote command?",
        a: "Under 2 seconds on a normal mobile connection. The BBC controller maintains a persistent connection to BBC Trust — there is no polling delay.",
      },
      {
        q: "What about offline / no-internet sites?",
        a: "Schedules and policies are cached locally on the BBC controller. If connectivity drops, the gate continues enforcing the last-known policy. State changes sync the moment connectivity returns.",
      },
      {
        q: "Can I plug this into my existing parking-management system?",
        a: "Yes — BBC Trust exposes REST APIs, webhooks, and a simple websocket feed. Most parking-management systems integrate in under a day. We provide reference code and engineering support.",
      },
    ],
    cta: {
      heading: "Run a pay-per-use site? Let's wire it up.",
      body: "Tell us your site type, gate count, and payments stack — we'll design a remote-control deployment that pays back the security-guard cost in months, not years.",
      label: "Talk to an Operations Specialist",
    },
  },
};

/* ===================== COMPONENT ===================== */

export default function UseCase() {
  const { slug } = useParams();
  const uc = USE_CASES[slug];

  /* All hooks must be called unconditionally before any early return.
     If the slug is unknown, fall through to the Navigate. */
  const jsonLd = uc
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: uc.h1,
        description: uc.metaDesc,
        image: uc.image,
        author: { "@type": "Organization", name: "Boom Barrier Company" },
        publisher: {
          "@type": "Organization",
          name: "Boom Barrier Company",
          logo: { "@type": "ImageObject", url: "https://www.boombarriercompany.com/branded/bbc-pro.png" },
        },
        mainEntityOfPage: uc.canonical,
        about: uc.keyword,
      }
    : null;

  useSeo({
    title: uc?.metaTitle || "Use Cases · Boom Barrier Company",
    description: uc?.metaDesc,
    canonical: uc?.canonical,
    image: uc?.image,
    jsonLd,
  });

  if (!uc) return <Navigate to="/" replace />;

  const Icon = uc.icon;

  return (
    <div data-testid="usecase-root" className="min-h-screen bg-[#0F1113] text-white font-body antialiased">
      <PlatformShellHeader />

      <main className="pt-8 md:pt-12">
        {/* HERO */}
        <section className="relative py-16 md:py-24 overflow-hidden border-b border-white/5">
          <div className="absolute inset-0 bbc-grid-bg opacity-[0.06] pointer-events-none" />
          <div
            className="absolute -top-32 right-0 w-[600px] h-[600px] rounded-full blur-3xl pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(0,255,133,0.08), transparent 60%)" }}
          />
          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <nav data-testid="usecase-breadcrumb" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#71717A]">
              <Link to="/" className="hover:text-white transition">Home</Link>
              <span>/</span>
              <span className="text-white/80">Use Cases</span>
              <span>/</span>
              <span className="text-[#00FF85]">{uc.eyebrow}</span>
            </nav>
            <div className="mt-10 grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 border border-[#00FF85]/30 bg-[#00FF85]/[0.06] flex items-center justify-center">
                    <Icon size={18} className="text-[#00FF85]" />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00FF85]">
                    {uc.eyebrow}
                  </span>
                </div>
                <h1
                  data-testid="usecase-h1"
                  className="font-display mt-6 text-white text-4xl md:text-5xl lg:text-6xl font-black tracking-[-0.02em] leading-[1.02]"
                >
                  {uc.h1}
                </h1>
                <p className="mt-6 text-[#A1A1AA] text-base md:text-lg font-body leading-relaxed max-w-3xl">
                  {uc.sub}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/quote"
                    data-testid="usecase-cta-primary"
                    className="inline-flex items-center gap-2 bg-[#00FF85] hover:bg-[#00CC6A] text-black font-semibold px-5 py-3 text-xs uppercase tracking-wider transition shadow-[0_0_18px_rgba(0,255,133,0.25)]"
                  >
                    {uc.cta.label}
                    <ArrowRight size={14} />
                  </Link>
                  <Link
                    to="/#partners"
                    data-testid="usecase-cta-secondary"
                    className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white/85 hover:text-white font-mono px-5 py-3 text-[11px] uppercase tracking-[0.22em] transition"
                  >
                    See the Platform
                    <ArrowUpRight size={13} />
                  </Link>
                </div>
              </div>
              {/* Right rail — what BBC ships */}
              <aside className="lg:col-span-4 border border-white/10 bg-[#16191C] p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00FF85]">
                  /// — What BBC ships
                </div>
                <ul className="mt-5 space-y-3.5">
                  {[
                    { icon: KeyRound, t: "Remote payment-lock", d: "Per-token, per-zone or per-gate." },
                    { icon: Smartphone, t: "BBC Trust app", d: "Open / close / lock from anywhere." },
                    { icon: Cable, t: "API + webhook hooks", d: "Plug into your CRM, ERP or payments stack." },
                    { icon: Lock, t: "Signed audit log", d: "Every action timestamped & exportable." },
                    { icon: ShieldCheck, t: "Fail-safe controller", d: "Caches policy when offline. Never fails open." },
                  ].map((it, i) => {
                    const I = it.icon;
                    return (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-8 h-8 border border-white/10 flex items-center justify-center shrink-0">
                          <I size={13} className="text-[#00FF85]" />
                        </div>
                        <div>
                          <div className="font-display text-white text-[13px] font-bold tracking-tight">{it.t}</div>
                          <div className="font-body text-[#A1A1AA] text-[12px] leading-relaxed">{it.d}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </aside>
            </div>
          </div>
        </section>

        {/* PROBLEM */}
        <section className="py-20 md:py-24 border-b border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">/ 01 — The Problem</div>
            <h2 className="font-display mt-4 text-white text-3xl md:text-4xl lg:text-5xl font-black tracking-[-0.02em] leading-[1.05] max-w-4xl">
              {uc.problem.title}
            </h2>
            <p className="mt-6 max-w-3xl text-[#A1A1AA] text-base md:text-lg font-body leading-relaxed">
              {uc.problem.body}
            </p>
            <ul className="mt-10 grid md:grid-cols-2 gap-3">
              {uc.problem.pains.map((p, i) => (
                <li
                  key={i}
                  data-testid={`usecase-pain-${i}`}
                  className="border border-white/10 bg-[#16191C] p-5 flex items-start gap-3"
                >
                  <AlertTriangle size={14} className="text-[#FBBF24] mt-0.5 shrink-0" />
                  <span className="text-white/85 text-sm font-body leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* SOLUTION */}
        <section className="py-20 md:py-24 border-b border-white/5 bg-[#13161A]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00FF85]">/ 02 — The BBC Approach</div>
            <h2 className="font-display mt-4 text-white text-3xl md:text-4xl lg:text-5xl font-black tracking-[-0.02em] leading-[1.05] max-w-4xl">
              {uc.solution.title}
            </h2>
            <p className="mt-6 max-w-3xl text-[#A1A1AA] text-base md:text-lg font-body leading-relaxed">
              {uc.solution.body}
            </p>
            <ol className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
              {uc.solution.steps.map((s, i) => (
                <li
                  key={i}
                  data-testid={`usecase-step-${i}`}
                  className="relative border border-white/10 bg-[#16191C] p-6"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00FF85]">Step.{s.n}</div>
                  <h3 className="font-display mt-3 text-white text-lg md:text-xl font-black tracking-tight leading-snug">{s.t}</h3>
                  <p className="mt-3 text-[#A1A1AA] text-sm font-body leading-relaxed">{s.d}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* WHY THIS WORKS */}
        <section className="py-20 md:py-24 border-b border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">/ 03 — Why It Works</div>
            <h2 className="font-display mt-4 text-white text-3xl md:text-4xl lg:text-5xl font-black tracking-[-0.02em] leading-[1.05] max-w-4xl">
              {uc.why.title}
            </h2>
            <ul className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {uc.why.points.map((p, i) => {
                const I = p.icon;
                return (
                  <li
                    key={i}
                    data-testid={`usecase-why-${i}`}
                    className="border border-white/10 bg-[#16191C] p-6 hover:border-[#00FF85]/40 transition"
                  >
                    <div className="w-9 h-9 border border-[#00FF85]/30 bg-[#00FF85]/[0.06] flex items-center justify-center">
                      <I size={14} className="text-[#00FF85]" />
                    </div>
                    <h3 className="font-display mt-5 text-white text-base md:text-lg font-black tracking-tight">{p.t}</h3>
                    <p className="mt-2 text-[#A1A1AA] text-sm font-body leading-relaxed">{p.d}</p>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* FAQ / OBJECTIONS */}
        <section className="py-20 md:py-24 border-b border-white/5 bg-[#13161A]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">/ 04 — Honest Answers</div>
            <h2 className="font-display mt-4 text-white text-3xl md:text-4xl lg:text-5xl font-black tracking-[-0.02em] leading-[1.05] max-w-4xl">
              Questions decision-makers actually ask.
            </h2>
            <div className="mt-12 grid md:grid-cols-2 gap-3">
              {uc.objections.map((o, i) => (
                <article
                  key={i}
                  data-testid={`usecase-faq-${i}`}
                  className="border border-white/10 bg-[#16191C] p-6"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircle2 size={14} className="text-[#00FF85] mt-0.5 shrink-0" />
                    <h3 className="font-display text-white text-base md:text-lg font-bold tracking-tight leading-snug">{o.q}</h3>
                  </div>
                  <p className="mt-4 ml-7 text-[#A1A1AA] text-sm font-body leading-relaxed">{o.a}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-28">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="border border-[#00FF85]/40 bg-gradient-to-br from-[#16191C] to-[#13161A] p-8 md:p-12 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full blur-3xl pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(0,255,133,0.15), transparent 60%)" }}
              />
              <div className="relative">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00FF85]">/ Next Step</div>
                <h2 className="font-display mt-3 text-white text-3xl md:text-4xl lg:text-5xl font-black tracking-[-0.02em] leading-[1.05] max-w-3xl">
                  {uc.cta.heading}
                </h2>
                <p className="mt-5 max-w-2xl text-[#A1A1AA] text-base md:text-lg font-body leading-relaxed">
                  {uc.cta.body}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/quote"
                    data-testid="usecase-cta-final"
                    className="inline-flex items-center gap-2 bg-[#00FF85] hover:bg-[#00CC6A] text-black font-semibold px-6 py-3.5 text-xs uppercase tracking-wider transition shadow-[0_0_18px_rgba(0,255,133,0.25)]"
                  >
                    {uc.cta.label}
                    <ArrowRight size={14} />
                  </Link>
                  <Link
                    to="/insights"
                    className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white/85 hover:text-white font-mono px-6 py-3.5 text-[11px] uppercase tracking-[0.22em] transition"
                  >
                    Read More Insights
                    <ArrowUpRight size={13} />
                  </Link>
                </div>
              </div>
            </div>

            {/* Related use cases */}
            <div className="mt-14">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">/// — Related verticals</div>
              <div className="mt-5 grid md:grid-cols-2 gap-3">
                {Object.entries(USE_CASES)
                  .filter(([s]) => s !== slug)
                  .map(([s, other]) => {
                    const OI = other.icon;
                    return (
                      <Link
                        key={s}
                        to={`/use-cases/${s}`}
                        data-testid={`usecase-related-${s}`}
                        className="group border border-white/10 bg-[#16191C] p-5 hover:border-[#00FF85]/40 transition flex items-start gap-4"
                      >
                        <div className="w-10 h-10 border border-[#00FF85]/30 bg-[#00FF85]/[0.06] flex items-center justify-center shrink-0">
                          <OI size={15} className="text-[#00FF85]" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#00FF85]">{other.eyebrow}</div>
                          <div className="font-display mt-1.5 text-white text-base font-bold tracking-tight leading-snug">{other.h1}</div>
                          <div className="mt-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-white/60 group-hover:text-[#00FF85] group-hover:gap-2 transition-all">
                            Read use case
                            <ArrowRight size={11} />
                          </div>
                        </div>
                      </Link>
                    );
                  })}
              </div>
            </div>
          </div>
        </section>
      </main>

      <PlatformShellFooter />
    </div>
  );
}
